import {
  Command,
  ViewDocumentSelection,
  IconEraser,
  IconPencil,
  ButtonExecuteEvent,
  ButtonView,
  WidgetToolbarRepository,
} from "ckeditor5";
import { decode, encode } from "html-entities";
import React, { useState } from "react";
import { css } from "styled-components";

import {
  confirmableConfirm,
  ConfirmableProps,
  ConfirmModal,
  Modal,
  SubmitButtons,
} from "@examspark/common-ui";
import { IconSpeechBubbleLine } from "@examspark/common-ui/dist/components/icons";

import RichContentEditor from "../components/RichContentEditor";
import { findCommonAncestorElement } from "../util/document";
import WrapInElementCommand from "../util/WrapInElementCommand";

import { PluginWithStyles } from "./PluginWithStyles";

export const COMMAND_TOGGLE_CONTEXT = "toggleContext";
export const COMMAND_EDIT_CONTEXT = "editContext";
export const UI_BUTTON_CONTEXT = "context";

const ELEMENT_WITH_CONTEXT = "withContext";
const ATTRIBUTE_MODEL_CONTENT = "content";
const ATTRIBUTE_DATA_CONTEXT_CONTENT = "data-additional-context";
const CLASS_HAS_CONTEXT = "has-context";

export default class AdditionalContext extends PluginWithStyles {
  declare toggleCommand: WrapInElementCommand;
  declare editCommand: EditContextCommand;

  static buildEditorStyles() {
    return css`
      .${CLASS_HAS_CONTEXT} {
        border-bottom: 1px dotted grey;
      }
    `;
  }

  static get requires() {
    return [WidgetToolbarRepository];
  }

  init() {
    this.editor.model.schema.register(ELEMENT_WITH_CONTEXT, {
      allowIn: ["$block", "$content", "$blockObject"],
      allowChildren: "$text",
      allowAttributes: [ATTRIBUTE_MODEL_CONTENT],
      isInline: true,
      isContent: true,
      isLimit: true,
    });

    this.editor.editing.view.domConverter.registerInlineObjectMatcher({
      name: "span",
      classes: [CLASS_HAS_CONTEXT],
    });

    this.editor.data.htmlProcessor.domConverter.registerInlineObjectMatcher({
      name: "span",
      classes: [CLASS_HAS_CONTEXT],
    });

    this.editor.conversion.for("upcast").elementToElement({
      model: (e, { writer }) =>
        writer.createElement(ELEMENT_WITH_CONTEXT, {
          content: e.getAttribute(ATTRIBUTE_DATA_CONTEXT_CONTENT),
        }),
      view: {
        name: "span",
        attributes: ATTRIBUTE_DATA_CONTEXT_CONTENT,
      },
    });

    this.editor.conversion.for("dataDowncast").elementToElement({
      model: ELEMENT_WITH_CONTEXT,
      view: (e, { writer }) =>
        writer.createEditableElement("span", {
          [ATTRIBUTE_DATA_CONTEXT_CONTENT]: e.getAttribute(
            ATTRIBUTE_MODEL_CONTENT,
          ),
        }),
    });

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: ELEMENT_WITH_CONTEXT,
      view: {
        name: "span",
        classes: [CLASS_HAS_CONTEXT],
      },
    });

    this.editor.commands.add(
      COMMAND_TOGGLE_CONTEXT,
      (this.toggleCommand = new WrapInElementCommand(
        this.editor,
        ELEMENT_WITH_CONTEXT,
        true,
      )),
    );

    this.editor.commands.add(
      COMMAND_EDIT_CONTEXT,
      (this.editCommand = new EditContextCommand(this.editor)),
    );

    this.toggleCommand.on<ButtonExecuteEvent>(
      "execute",
      (e) => {
        if (e.return !== true) return;
        this.editCommand.execute();
      },
      {
        priority: "low",
      },
    );

    this.editor.ui.componentFactory.add(UI_BUTTON_CONTEXT, (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: this.editor.t("Additional Context"),
        icon: IconSpeechBubbleLine.Content,
        tooltip: true,
      });
      button
        .bind("isEnabled", "isOn")
        .to(this.toggleCommand, "isEnabled", "value");
      button.on("execute", () => this.toggleCommand.execute());
      return button;
    });

    this.createBalloonMenu();
  }

  private createBalloonMenu() {
    this.editor.ui.componentFactory.add("contextEditButton", (locale) => {
      const editButton = new ButtonView(locale);
      editButton.set({
        label: this.editor.t("Edit Context"),
        withText: false,
        icon: IconPencil,
      });
      editButton.bind("isEnabled").to(this.editCommand, "isEnabled");
      editButton.on("execute", () => this.editCommand.execute());
      return editButton;
    });

    this.editor.ui.componentFactory.add("contextRemoveButton", (locale) => {
      const removeButton = new ButtonView(locale);
      removeButton.set({
        label: this.editor.t("Remove Context"),
        withText: false,
        icon: IconEraser,
      });
      removeButton.on("execute", () => this.toggleCommand.execute());
      return removeButton;
    });

    this.editor.plugins.get(WidgetToolbarRepository).register("context", {
      ariaLabel: this.editor.t("Additional Context"),
      items: ["contextEditButton", "contextRemoveButton"],
      getRelatedElement: (selection) =>
        getSelectedContextElement(
          selection ?? this.editor.editing.view.document.selection,
        ),
    });
  }
}

export class EditContextCommand extends Command {
  async execute() {
    const viewElement = getSelectedContextElement(
      this.editor.editing.view.document.selection,
    );
    if (viewElement == null) return;
    const modelElement = this.editor.editing.mapper.toModelElement(viewElement);
    if (modelElement == null) return;

    const attributeValue = modelElement.getAttribute(
      ATTRIBUTE_MODEL_CONTENT,
    ) as string | undefined;

    let initialValue: string;

    if (attributeValue == null) {
      initialValue =
        this.editor.editing.view.domConverter.viewToDom(viewElement).innerText;
    } else {
      initialValue = decode(attributeValue);
    }

    let updatedValue = await confirmableConfirm(EditContextModal, {
      initialValue,
      editorIsReadOnly: this.editor.isReadOnly,
    });

    if (updatedValue === false) {
      // don't return here, we're handling the case where the user cancelled
      // the initial dialog
      updatedValue = initialValue;
    }

    this.editor.model.change((writer) => {
      if (modelElement == null) return;

      writer.setAttribute(
        ATTRIBUTE_MODEL_CONTENT,
        encode(updatedValue),
        modelElement,
      );
    });
  }
}

export const EditContextModal = ({
  initialValue,
  editorIsReadOnly,
  ...props
}: ConfirmableProps<false | string> & {
  initialValue: string;
  editorIsReadOnly: boolean;
}) => {
  const [value, setValue] = useState(initialValue);

  return (
    <ConfirmModal.Generic<false | string>
      defaultResult={false}
      title="Additional Context"
      size="large"
      {...props}
      buttons={({ onResolve }) => (
        <SubmitButtons
          onSubmit={() => onResolve(value)}
          onCancel={() => onResolve(false)}
          submitTooltip={editorIsReadOnly ? "Editor is read-only" : "Save"}
          isSubmitDisabled={editorIsReadOnly}
        />
      )}
    >
      <Modal.PaddedBody>
        <RichContentEditor
          value={value}
          onChange={(value) => setValue(value ?? "")}
          options={() => ({})}
        />
      </Modal.PaddedBody>
    </ConfirmModal.Generic>
  );
};

const getSelectedContextElement = (selection?: ViewDocumentSelection) => {
  return findCommonAncestorElement(selection?.getFirstRange(), (e) =>
    e.hasClass(CLASS_HAS_CONTEXT),
  );
};
